import APIRequest from './APIRequest';
import { getLocation } from './locationService';

const API_KEY = '9360561a1d44a2c25117f8ac7c9226e4e12397f14967ec55a402740cb8549b29';

/** Forecast APIs can be slow; use a generous per-request timeout (ms). */
const FORECAST_TIMEOUT_MS = 60000;

const forecastRequestConfig = {
  headers: { 'x-api-key': API_KEY },
  timeout: FORECAST_TIMEOUT_MS,
};

/** Ensure apisauce/axios response is OK before returning payload. */
function unwrapForecastResponse(response, label) {
  if (!response) {
    throw new Error(`${label}: no response`);
  }
  if (
    response.problem === 'TIMEOUT_ERROR' ||
    response.problem === 'ECONNABORTED'
  ) {
    throw new Error(
      `${label}: request timed out after ${FORECAST_TIMEOUT_MS / 1000}s`
    );
  }
  if (!response.ok) {
    const msg =
      response.data?.error ||
      response.data?.message ||
      response.problem ||
      `HTTP ${response.status}`;
    throw new Error(`${label}: ${msg}`);
  }
  if (response.data == null) {
    throw new Error(`${label}: empty response body`);
  }
  return response.data;
}

export const forecastService = {
  // Get next 7 days forecast
  getNext7DaysForecast: async (location) => {
    try {
      const response = await APIRequest.get(
        `/web/forecast?location=${location}&from=today&to=+8days&interval=24h`,
        {},
        forecastRequestConfig
      );
      return unwrapForecastResponse(response, 'Daily forecast');
    } catch (error) {
      console.error('Error fetching next 7 days forecast:', error);
      throw error;
    }
  },

  // Get previous 7 days forecast
  getPrevious7DaysForecast: async (location) => {
    try {
      const response = await APIRequest.get(
        `/web/forecast?location=${location}&to=now&from=-7days&interval=24h`,
        {},
        forecastRequestConfig
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching previous 7 days forecast:', error);
      throw error;
    }
  },

  // Get hourly forecast (will be implemented later)
  getHourlyForecast: async (location, hours = 24) => {
    try {
      const response = await APIRequest.get(
        `/web/forecast?location=${location}&from=now&to=+${hours}hours&interval=1h`,
        {},
        forecastRequestConfig
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching hourly forecast:', error);
      throw error;
    }
  },

  // Get previous hourly forecast (will be implemented later)
  getPreviousHourlyForecast: async (location, hours = 24) => {
    try {
      const response = await APIRequest.get(
        `/web/forecast?location=${location}&to=now&for=-${hours}hours&interval=1h`,
        {},
        forecastRequestConfig
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching previous hourly forecast:', error);
      throw error;
    }
  },

  /** Generic forecast fetch for dashboard interval tabs */
  getForecast: async (location, { from, to, interval, limit }) => {
    try {
      const limitParam = limit ? `&limit=${limit}` : '';
      const response = await APIRequest.get(
        `/web/forecast?location=${location}&from=${from}&to=${to}&interval=${interval}${limitParam}`,
        {},
        forecastRequestConfig
      );
      return unwrapForecastResponse(response, 'Forecast');
    } catch (error) {
      console.error('Error fetching forecast:', error);
      throw error;
    }
  },

  // Get current weather (for=now)
  getCurrentWeather: async (location) => {
    try {
      const response = await APIRequest.get(
        `/web/current-weather?location=${location}&for=now`,
        {},
        forecastRequestConfig
      );
      return unwrapForecastResponse(response, 'Current weather');
    } catch (error) {
      console.error('Error fetching current weather:', error);
      throw error;
    }
  }
};

/** Normalize precipitation chance to 0–100 for UI. */
function normalizePopPercent(pop) {
  if (typeof pop !== 'number' || Number.isNaN(pop)) return 0;
  if (pop > 0 && pop <= 1) return Math.round(pop * 100);
  return Math.round(pop);
}

/**
 * Map a forecast period (daily or current-weather) to dashboard `current` shape.
 */
export function mapPeriodToCurrentWeather(period, place = null) {
  if (!period) return { current: null, place: place ?? null };

  const minF = period.minTempF ?? period.tempMinF;
  const maxF = period.maxTempF ?? period.tempMaxF;
  let tempF = typeof period.tempF === 'number' ? period.tempF : null;
  if (tempF == null && typeof minF === 'number' && typeof maxF === 'number') {
    tempF = Math.round((minF + maxF) / 2);
  } else if (tempF == null && typeof maxF === 'number') {
    tempF = maxF;
  }

  const current = {
    dt: period.timestamp,
    dateTimeISO: period.dateTimeISO || period.validTime,
    tempC: period.tempC,
    tempF,
    feels_like: period.feelslikeC,
    humidity: period.humidity,
    pressure: period.pressureMB,
    wind_speed: period.windSpeedMPS,
    windSpeedMPH: period.windSpeedMPH,
    wind_deg: period.windDirDEG,
    wind_gust: period.windGustMPS,
    pop: normalizePopPercent(period.pop),
    perciption: normalizePopPercent(period.pop),
    visibilityKM:
      typeof period.visibilityKM === 'number' ? period.visibilityKM : null,
    visibility:
      (typeof period.visibilityKM === 'number' ? period.visibilityKM : 0) *
      1000,
    clouds: period.sky,
    weatherPrimary: period.weatherPrimary || period.weather,
    weather: [
      {
        id: 800,
        main: period.weatherPrimary || 'Clear',
        description: period.weather || period.weatherPrimary || 'Clear sky',
        icon: period.icon ? period.icon.replace(/\.png$/i, '') : '01d',
      },
    ],
    uvi:
      typeof period.uvi === 'number' && !Number.isNaN(period.uvi)
        ? period.uvi
        : null,
    dewpointF: typeof period.dewpointF === 'number' ? period.dewpointF : null,
    dewpointC: typeof period.dewpointC === 'number' ? period.dewpointC : null,
    windGustMPH:
      typeof period.windGustMPH === 'number' ? period.windGustMPH : null,
    feelslikeF: typeof period.feelslikeF === 'number' ? period.feelslikeF : null,
    windSpeedMinMPH: period.windSpeedMinMPH,
    windSpeedMaxMPH: period.windSpeedMaxMPH,
  };

  return { current, place: place ?? null };
}

/** Today's daily period → current weather for dashboard overview. */
export function mapTodayForecastToCurrent(
  dailyApiResponse,
  referenceDate = new Date()
) {
  const block = dailyBlockFromResponse(dailyApiResponse);
  const timeZone = resolveForecastTimeZone(block);
  const today = findTodayDailyPeriod(
    block?.periods ?? [],
    referenceDate,
    timeZone
  );
  return mapPeriodToCurrentWeather(today, block?.place ?? null);
}

/** Daily forecast block: `{ success, response }` or nested under `.data`. */
function dailyBlockFromResponse(apiResponse) {
  if (!apiResponse) return null;
  return apiResponse?.response?.[0] ?? apiResponse?.data?.response?.[0] ?? null;
}

function dailyPeriodsFromResponse(apiResponse) {
  return dailyBlockFromResponse(apiResponse)?.periods ?? [];
}

/** Calendar Y-M-D in the forecast location's IANA timezone (from API `profile.tz`, per location). */
export function getCalendarPartsInTimeZone(date, timeZone) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || undefined,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    return {
      year: parts.find((p) => p.type === 'year')?.value,
      month: parts.find((p) => p.type === 'month')?.value,
      day: parts.find((p) => p.type === 'day')?.value,
    };
  } catch {
    return {
      year: String(date.getFullYear()),
      month: String(date.getMonth() + 1).padStart(2, '0'),
      day: String(date.getDate()).padStart(2, '0'),
    };
  }
}

function sameCalendarDayInZone(isoOrDate, referenceDate, timeZone) {
  const iso = typeof isoOrDate === 'string' ? isoOrDate : isoOrDate?.dateTimeISO || isoOrDate?.validTime;
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const a = getCalendarPartsInTimeZone(d, timeZone);
  const b = getCalendarPartsInTimeZone(referenceDate, timeZone);
  return a.year === b.year && a.month === b.month && a.day === b.day;
}

/**
 * Find the daily period for "today" in the weather location's timezone.
 * Falls back to the first period with sunrise/sunset, then periods[0].
 */
export function findTodayDailyPeriod(
  periods,
  referenceDate = new Date(),
  timeZone = null
) {
  if (!Array.isArray(periods) || periods.length === 0) return null;

  const tz =
    timeZone ||
    (typeof Intl !== 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : undefined);

  const todayMatch = periods.find((p) =>
    sameCalendarDayInZone(p.dateTimeISO || p.validTime, referenceDate, tz)
  );
  if (todayMatch) return todayMatch;

  const withSun = periods.find((p) => p.sunrise != null && p.sunset != null);
  return withSun ?? periods[0];
}

/** Parse sunrise/sunset instants from a daily forecast period (for arc progress). */
export function sunTimesFromDailyPeriod(period) {
  if (!period) return { sunrise: null, sunset: null };

  const parseIso = (iso) => {
    if (!iso || typeof iso !== 'string') return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d;
  };
  const parseUnix = (ts) => {
    const n = Number(ts);
    if (!Number.isFinite(n)) return null;
    const d = new Date(n * 1000);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const sunrise = parseIso(period.sunriseISO) ?? parseUnix(period.sunrise);
  const sunset = parseIso(period.sunsetISO) ?? parseUnix(period.sunset);

  return { sunrise, sunset };
}

/**
 * Time label from API ISO (e.g. "2026-05-15T05:43:34-05:00") — clock is already local to that location.
 * Used when `profile.tz` is missing but ISO strings are present.
 */
export function formatTimeFromForecastIso(iso) {
  if (!iso || typeof iso !== 'string') return null;
  const m = iso.match(/T(\d{1,2}):(\d{2})/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = m[2];
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${min} ${ampm}`;
}

/** IANA timezone for this forecast location — always from API, never hardcoded. */
export function resolveForecastTimeZone(dailyBlock, todayPeriod = null) {
  return (
    dailyBlock?.profile?.tz ||
    dailyBlock?.profile?.timezone ||
    null
  );
}

/** Short label for accordion row (Sunny, Cloudy, Rain, …). */
export function shortWeatherCondition(weatherPrimary) {
  if (!weatherPrimary) return "—";
  const w = String(weatherPrimary).toLowerCase();
  if (
    w.includes("rain") ||
    w.includes("shower") ||
    w.includes("drizzle") ||
    w.includes("storm")
  ) {
    return "Rain";
  }
  if (w.includes("sun") || w.includes("clear")) return "Sunny";
  if (w.includes("cloud") || w.includes("overcast") || w.includes("fair")) {
    return "Cloudy";
  }
  return weatherPrimary.split(/\s+/)[0];
}

function iconKeyFromPeriod(period) {
  const raw = period?.icon;
  if (!raw || typeof raw !== "string") return "cloudy";
  return raw.replace(/\.png$/i, "");
}

function toRoundedFahrenheit(value) {
  return typeof value === 'number' && !Number.isNaN(value)
    ? Math.round(value)
    : null;
}

function celsiusToRoundedFahrenheit(value) {
  return typeof value === 'number' && !Number.isNaN(value)
    ? Math.round((value * 9) / 5 + 32)
    : null;
}

function resolvePeriodTempF(period) {
  const direct =
    toRoundedFahrenheit(period.tempF) ??
    toRoundedFahrenheit(period.avgTempF);

  if (direct != null) return direct;

  const maxF = toRoundedFahrenheit(period.maxTempF ?? period.tempMaxF);
  const minF = toRoundedFahrenheit(period.minTempF ?? period.tempMinF);
  if (maxF != null && minF != null) return Math.round((maxF + minF) / 2);
  if (maxF != null) return maxF;
  if (minF != null) return minF;

  const fromC =
    celsiusToRoundedFahrenheit(period.tempC) ??
    celsiusToRoundedFahrenheit(period.avgTempC);

  if (fromC != null) return fromC;

  const maxC = celsiusToRoundedFahrenheit(period.maxTempC ?? period.tempMaxC);
  const minC = celsiusToRoundedFahrenheit(period.minTempC ?? period.tempMinC);
  if (maxC != null && minC != null) return Math.round((maxC + minC) / 2);
  if (maxC != null) return maxC;
  if (minC != null) return minC;

  return null;
}

/**
 * Map daily forecast API periods to accordion rows (max 7 days).
 */
export function mapSevenDayForecastRows(
  dailyApiResponse,
  referenceDate = new Date()
) {
  const block = dailyBlockFromResponse(dailyApiResponse);
  const periods = (block?.periods ?? []).slice(0, 7);
  const timeZone = resolveForecastTimeZone(block);
  if (!periods.length) return { days: [], weekMinF: null, weekMaxF: null, timeZone };

  const mins = [];
  const maxs = [];
  for (const p of periods) {
    const lo = p.minTempF ?? p.tempMinF;
    const hi = p.maxTempF ?? p.tempMaxF;
    if (typeof lo === "number") mins.push(lo);
    if (typeof hi === "number") maxs.push(hi);
  }
  const weekMinF = mins.length ? Math.min(...mins) : null;
  const weekMaxF = maxs.length ? Math.max(...maxs) : null;

  const days = periods.map((period) => {
    const minF = period.minTempF ?? period.tempMinF ?? null;
    const maxF = period.maxTempF ?? period.tempMaxF ?? null;
    const iso = period.dateTimeISO || period.validTime;
    const isToday = sameCalendarDayInZone(iso, referenceDate, timeZone);

    let dayLabel = "—";
    if (isToday) {
      dayLabel = "Today";
    } else if (iso) {
      try {
        dayLabel = new Date(iso).toLocaleDateString("en-US", {
          weekday: "short",
          timeZone: timeZone || undefined,
        });
      } catch {
        dayLabel = new Date(iso).toLocaleDateString("en-US", { weekday: "short" });
      }
    }

    let dateLabel = "—";
    if (iso) {
      try {
        dateLabel = new Date(iso).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          timeZone: timeZone || undefined,
        });
      } catch {
        dateLabel = new Date(iso).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
      }
    }

    const { sunrise, sunset } = sunTimesFromDailyPeriod(period);
    const formatSun = (date, iso) => {
      if (timeZone && date instanceof Date && !Number.isNaN(date.getTime())) {
        return date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
          timeZone,
        });
      }
      return formatTimeFromForecastIso(iso) ?? "—";
    };

    const pop =
      typeof period.pop === "number" && !Number.isNaN(period.pop)
        ? Math.round(period.pop)
        : null;

    return {
      id: iso || String(period.timestamp),
      dayLabel,
      isToday,
      condition: shortWeatherCondition(period.weatherPrimary || period.weather),
      weatherPrimary: period.weatherPrimary || period.weather || "—",
      iconKey: iconKeyFromPeriod(period),
      pop,
      minF,
      maxF,
      humidity:
        typeof period.humidity === "number" ? Math.round(period.humidity) : null,
      windMph:
        typeof period.windSpeedMPH === "number"
          ? Math.round(period.windSpeedMPH)
          : null,
      uvi:
        typeof period.uvi === "number" && !Number.isNaN(period.uvi)
          ? Math.round(period.uvi)
          : null,
      dateLabel,
      sunriseLabel: formatSun(sunrise, period.sunriseISO),
      sunsetLabel: formatSun(sunset, period.sunsetISO),
    };
  });

  return { days, weekMinF, weekMaxF, timeZone };
}

/**
 * Extract today's sunrise/sunset from the 8-day daily forecast API response.
 * `timeZone` comes from `response[0].profile.tz` for whichever location was requested.
 */
export function extractTodaySunTimesFromDailyForecast(
  dailyApiResponse,
  referenceDate = new Date()
) {
  const block = dailyBlockFromResponse(dailyApiResponse);
  const periods = block?.periods ?? [];
  const timeZone = resolveForecastTimeZone(block);
  const today = findTodayDailyPeriod(periods, referenceDate, timeZone);
  const { sunrise, sunset } = sunTimesFromDailyPeriod(today);
  return {
    sunrise,
    sunset,
    timeZone,
    sunriseIso: today?.sunriseISO ?? null,
    sunsetIso: today?.sunsetISO ?? null,
  };
}

/** Extract periods block from forecast API payload */
export function extractForecastBlock(apiResponse) {
  if (!apiResponse) return { periods: [], place: null, timeZone: null };
  const block =
    apiResponse?.response?.[0] ?? apiResponse?.data?.response?.[0] ?? null;
  return {
    periods: block?.periods ?? [],
    place: block?.place ?? null,
    timeZone:
      block?.profile?.tz || block?.profile?.timezone || null,
  };
}

/** Card/chart label from period timestamp */
export function formatForecastCardLabel(
  period,
  timeZone,
  intervalId = '1h_24h'
) {
  const iso = period?.dateTimeISO || period?.validTime;
  const ts = period?.timestamp;
  const d = iso
    ? new Date(iso)
    : ts != null
      ? new Date(Number(ts) * 1000)
      : null;
  if (!d || Number.isNaN(d.getTime())) return '—';

  const opts = { timeZone: timeZone || undefined };

  if (intervalId === '1d_7d') {
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      ...opts,
    });
  }

  const weekday = d.toLocaleDateString('en-US', { weekday: 'short', ...opts });
  const time = d
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: intervalId?.startsWith('5min_') ? '2-digit' : undefined,
      hour12: true,
      ...opts,
    })
    .replace(/\s/g, '');
  return `${weekday} ${time}`;
}

/** Map raw API periods → unified points for new Forecast UI */
export function mapForecastPoints(periods, timeZone, intervalId) {
  if (!Array.isArray(periods)) return [];

  return periods.map((period, index) => {
    const tempF = resolvePeriodTempF(period);

    const windMph =
      typeof period.windSpeedMPH === 'number'
        ? Math.round(period.windSpeedMPH)
        : typeof period.windSpeedMPS === 'number'
          ? Math.round(period.windSpeedMPS * 2.237)
          : 0;

    const iconKey = period.icon
      ? String(period.icon).replace(/\.png$/i, '')
      : 'cloudy';

    return {
      id:
        period.dateTimeISO ||
        period.validTime ||
        String(period.timestamp ?? index),
      timestamp: period.timestamp,
      dateTimeISO: period.dateTimeISO || period.validTime,
      label: formatForecastCardLabel(period, timeZone, intervalId),
      tempF,
      popPercent: normalizePopPercent(period.pop),
      windMph,
      iconKey,
      weatherPrimary: period.weatherPrimary || period.weather || '',
    };
  });
}

// Helper function to format forecast data
export const formatForecastData = (apiResponse) => {
  if (!apiResponse?.success || !apiResponse?.data?.response?.[0]?.periods) {
    return { daily: [], hourly: [] };
  }

  const periods = apiResponse.data.response[0].periods;
  const location = apiResponse.data.response[0].place;

  // Format daily data to match component expectations
  // Remove duplicates based on date
  const uniquePeriods = periods.reduce((acc, period) => {
    const date = new Date(period.timestamp * 1000).toDateString();
    if (!acc.find(p => new Date(p.timestamp * 1000).toDateString() === date)) {
      acc.push(period);
    }
    return acc;
  }, []);

  const dailyData = uniquePeriods.map(period => ({
    dt: period.timestamp, // Unix timestamp for date formatting
    date: new Date(period.timestamp * 1000).toLocaleDateString(),
    dateTimeISO: period.dateTimeISO,
    timestamp: period.timestamp,
    temp: {
      min: period.tempC, // Using tempC as both min and max for daily view
      max: period.tempC + 5, // Adding some variation for display
      day: period.tempC,
      night: period.tempC - 3,
      eve: period.tempC + 1,
      morn: period.tempC - 1
    },
    tempC: period.tempC,
    tempF: period.tempF,
    feels_like: {
      day: period.feelslikeC,
      night: period.feelslikeC - 3,
      eve: period.feelslikeC + 1,
      morn: period.feelslikeC - 1
    },
    humidity: period.humidity,
    pressure: period.pressureMB,
    wind_speed: period.windSpeedMPS, // Convert to m/s
    windSpeedMPH: period.windSpeedMPH,
    wind_deg: period.windDirDEG,
    wind_gust: period.windGustMPS,
    pop: period.pop / 100, // Convert percentage to decimal
    perciption: period.precipMM,
    rain: period.precipMM > 0 ? { '1h': period.precipMM } : {},
    snow: period.snowCM > 0 ? { '1h': period.snowCM } : {},
    visibility: period.visibilityKM * 1000, // Convert to meters
    clouds: period.sky,
    weather: [{
      id: 800,
      main: period.weatherPrimary || 'Clear',
      description: period.weather || 'Clear sky',
      icon: period.icon ? period.icon.replace('.png', '') : '01d'
    }],
    uvi: period.uvi || 0
  }));

  // Format hourly data (for now, using daily data as hourly)
  const hourlyData = periods.map(period => ({
    dt: period.timestamp,
    temp: period.tempC,
    feels_like: period.feelslikeC,
    humidity: period.humidity,
    pressure: period.pressureMB,
    wind_speed: period.windSpeedMPS,
    wind_deg: period.windDirDEG,
    wind_gust: period.windGustMPS,
    pop: period.pop / 100,
    rain: period.precipMM > 0 ? { '1h': period.precipMM } : {},
    snow: period.snowCM > 0 ? { '1h': period.snowCM } : {},
    visibility: period.visibilityKM * 1000,
    clouds: period.sky,
    weather: [{
      id: 800,
      main: period.weatherPrimary || 'Clear',
      description: period.weather || 'Clear sky',
      icon: period.icon ? period.icon.replace('.png', '') : '01d'
    }],
    uvi: period.uvi || 0
  }));

  return {
    location,
    daily: dailyData,
    hourly: hourlyData
  };
};
